/**
 * Report wizard — UX/UI Specification §9.11.
 *
 * Twelve steps. All wizard state lives in one client-side object and survives
 * moving backward; nothing is persisted until the final Generate, because
 * there is no draft status in the MVP (PRD §15.5).
 *
 * No step blocks on missing optional data. Step 9 warns; it never gates
 * (PRD §18).
 *
 * The preview renders the real template from the real snapshot, built with the
 * same `buildSnapshot` the Worker uses at generation time — so what the
 * instructor approves is what the student receives.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ASSESSMENT_KEYS,
  ASSESSMENT_LABELS,
  buildSnapshot,
  collectWarnings,
  COVER_MESSAGE_MAX_LENGTH,
  defaultSelections,
  LIMITS,
  parseTypedNumber,
  formatDateLong,
  formatDelta,
  formatValue,
  PHOTO_CATEGORY_LABELS,
  POSTURE_COMPARISON_MAX,
  type AssessmentKey,
  type CoverMessage,
  type GoalRecord,
  type MetricKey,
  type AssessmentRecord,
  type PhotoRecord,
  type ReportSelections,
  type ReportSnapshot,
  type ReportSource,
  type SnapshotCustomMetric,
  type SnapshotPhoto,
} from '@pp/shared';
import { ReportDocument } from '@pp/report-template';
import '@pp/report-template/styles.css';
import { ApiError, api } from '../api/client.js';
import { messageFor } from '../api/error-messages.js';
import { Button } from '../components/ui/Button.js';
import { StudentPicker } from '../components/StudentPicker.js';
import { AssessmentForm } from '../components/AssessmentForm.js';
import { GoalForm } from '../components/GoalForm.js';
import { NumberField } from '../components/ui/NumberField.js';
import './ReportWizard.css';

interface GeneratedVersion {
  id: string;
  versionNumber: number;
  status: 'rendering' | 'ready' | 'failed';
  pdfUrl: string | null;
}

interface ReportDates {
  /** Ascending. Drives the "last two measurements" shortcut. */
  measurementDates: string[];
  /** Across measurements, photos and assessments alike. */
  earliest: string | null;
  latest: string | null;
}

interface PrepareResponse {
  source: ReportSource;
  selections: ReportSelections;
  warnings: { code: string; message: string }[];
  coverMessageOptions: CoverMessage[];
  photoUrls: Record<string, string>;
  profilePhotoUrl: string | null;
  logoUrl: string | null;
}

const STEPS = [
  'Öğrenci',
  'Dönem',
  'Ölçümler',
  'Fotoğraflar',
  'Postür Analizi',
  'Hedefler',
  'Değerlendirme',
  'Kapak Mesajı',
  'Paket',
  'Hazırlık',
  'Önizleme',
  'PDF',
] as const;

/** Whole days between two YYYY-MM-DD dates. */
function daysBetween(start: string, end: string): number {
  const ms = Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`);
  return Math.round(ms / 86400000);
}

function monthRange(): { start: string; end: string } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Istanbul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  const [year = '2026', month = '01'] = parts.split('-');
  const lastDay = new Date(Number(year), Number(month), 0).getDate();
  return {
    start: `${year}-${month}-01`,
    end: `${year}-${month}-${String(lastDay).padStart(2, '0')}`,
  };
}

export function ReportWizard() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [studentId, setStudentId] = useState(params.get('ogrenci') ?? '');
  const [studentName, setStudentName] = useState('');
  const [period, setPeriod] = useState(monthRange);

  /**
   * The dates the student has records on, loaded before the period is picked
   * so the wizard can offer a period instead of making the instructor go and
   * look one up.
   */
  const [dates, setDates] = useState<ReportDates | null>(null);

  const [prepared, setPrepared] = useState<PrepareResponse | null>(null);
  const [selections, setSelections] = useState<ReportSelections | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState<GeneratedVersion | null>(null);
  /*
   * One key per attempt, kept across retries of that attempt. Two taps on a
   * slow connection must not spend a second render or produce a second version
   * of the same report (API §26).
   */
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());

  const source = prepared?.source ?? null;

  const snapshot = useMemo(() => {
    if (!source || !selections) return null;
    return buildSnapshot(source, selections);
  }, [source, selections]);

  const warnings = useMemo(() => {
    if (!source || !snapshot) return [];
    return collectWarnings(source, snapshot);
  }, [source, snapshot]);

  /**
   * Every metric the student actually has data for, whether or not it is
   * currently selected. The snapshot only carries the selected ones, so driving
   * the checkbox list from it would make unticking a metric permanent.
   */
  const offeredMetrics = useMemo(() => {
    if (!source || !selections) return [];
    return buildSnapshot(source, {
      ...selections,
      includedMetrics: defaultSelections(source).includedMetrics,
    }).metrics;
  }, [source, selections]);

  /**
   * The student's own custom measurement fields, whether or not they are
   * currently selected — same trick as offeredMetrics above, forcing the
   * selection to the full set so unchecking one doesn't make its preview
   * values disappear from the list.
   */
  const offeredCustomMetrics = useMemo(() => {
    if (!source || !selections) return [];
    return buildSnapshot(source, {
      ...selections,
      includedCustomLabels: defaultSelections(source).includedCustomLabels,
    }).customMetrics;
  }, [source, selections]);

  /** Preview images go through the authorised media route (Doc 6 §15.4). */
  const resolveImage = useCallback(
    (photo: SnapshotPhoto): string | null => {
      if (!prepared) return null;
      if (photo.photoId === source?.student.id) return prepared.profilePhotoUrl;
      return prepared.photoUrls[photo.photoId] ?? null;
    },
    [prepared, source],
  );

  // Loaded on the period step rather than on selection: picking a student is
  // a browse, and a request per tap through the list would be wasteful.
  useEffect(() => {
    if (step !== 1 || studentId === '') return;
    let cancelled = false;
    setDates(null);
    api
      .get<ReportDates>(`/api/students/${studentId}/report-dates`)
      .then((data) => {
        if (!cancelled) setDates(data);
      })
      .catch(() => {
        if (!cancelled) {
          setDates({ measurementDates: [], earliest: null, latest: null });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [step, studentId]);

  /** The two periods worth offering, or null when the student has too little. */
  const shortcuts = useMemo(() => {
    if (!dates) return [];
    const out: { key: string; title: string; start: string; end: string }[] = [];

    const m = dates.measurementDates;
    if (m.length >= 2) {
      out.push({
        key: 'last-two',
        title: 'Son iki ölçüm arası',
        start: m[m.length - 2] as string,
        end: m[m.length - 1] as string,
      });
    }
    // Everything the student has, of any kind — which is the report D-01 used
    // to produce for every period, now asked for explicitly.
    if (dates.earliest && dates.latest && dates.earliest !== dates.latest) {
      out.push({
        key: 'all-time',
        title: 'Tüm zamanlar',
        start: dates.earliest,
        end: dates.latest,
      });
    }
    return out;
  }, [dates]);

  async function prepare() {
    setBusy(true);
    setError(undefined);
    try {
      const data = await api.post<PrepareResponse>('/api/reports/prepare', {
        studentId,
        periodStart: period.start,
        periodEnd: period.end,
      });
      setPrepared(data);
      setSelections(data.selections ?? defaultSelections(data.source));
      setStep(2);
    } catch (err) {
      setError(
        err instanceof ApiError ? messageFor(err.code) : messageFor('INTERNAL_ERROR'),
      );
    } finally {
      setBusy(false);
    }
  }

  /** The only place a PDF renderer ever runs (red line 4). */
  async function generate() {
    if (!selections) return;
    setGenerating(true);
    setError(undefined);

    try {
      const version = await api.post<GeneratedVersion>(
        '/api/reports/generate',
        { studentId, periodStart: period.start, periodEnd: period.end, selections },
        idempotencyKey,
      );
      setGenerated(version);
    } catch (err) {
      setError(
        err instanceof ApiError ? messageFor(err.code) : messageFor('INTERNAL_ERROR'),
      );
      // A failed attempt is finished. Trying again is a new attempt, or the
      // server would hand back the failure it remembered.
      setIdempotencyKey(crypto.randomUUID());
    } finally {
      setGenerating(false);
    }
  }

  function update(patch: Partial<ReportSelections>) {
    setSelections((current) => (current ? { ...current, ...patch } : current));
  }

  /*
   * The only step that can block. "Paketinizde  ders hakkınız kalmıştır." is
   * what a missing number prints on a page that goes to a student, so the
   * wizard refuses to carry it forward rather than quietly dropping it.
   */
  const packageIncomplete =
    selections !== null &&
    selections.packageReminder.enabled &&
    selections.packageReminder.remainingLessons === null;

  const canAdvance = step === 0 ? studentId !== '' : !(step === 8 && packageIncomplete);

  return (
    <section className="wiz">
      <header className="wiz__head">
        <h1>Rapor Oluştur</h1>
        {studentName && <p className="wiz__student">{studentName}</p>}
      </header>

      <ol className="wiz__steps" aria-label="Rapor adımları">
        {STEPS.map((label, index) => (
          <li
            key={label}
            className={
              index === step
                ? 'wiz__step wiz__step--on'
                : index < step
                  ? 'wiz__step wiz__step--done'
                  : 'wiz__step'
            }
            aria-current={index === step ? 'step' : undefined}
          >
            <span className="wiz__step-no">{index + 1}</span>
            <span className="wiz__step-label">{label}</span>
          </li>
        ))}
      </ol>

      {error && (
        <p className="wiz__error" role="alert">
          {error}
        </p>
      )}

      <div className="wiz__panel">
        {step === 0 && (
          <StudentPicker
            selectedId={studentId}
            onSelect={(id, name) => {
              setStudentId(id);
              setStudentName(name);
            }}
          />
        )}

        {step === 1 && (
          <div className="wiz__period">
            <h2 className="wiz__title">Raporlama Dönemi</h2>
            <p className="wiz__hint">
              <b>Rapora yalnızca bu tarihler arasındaki kayıtlar girer.</b> Dönem dışında
              kalan ölçüm, fotoğraf ve değerlendirmeler rapora yansımaz. Öğrencinin tüm
              geçmişini raporlamak için “Tüm zamanlar”ı seçin.
            </p>
            <div className="wiz__dates">
              <label>
                <span>Başlangıç</span>
                <input
                  type="date"
                  value={period.start}
                  onChange={(e) => setPeriod({ ...period, start: e.target.value })}
                />
              </label>
              <label>
                <span>Bitiş</span>
                <input
                  type="date"
                  value={period.end}
                  onChange={(e) => setPeriod({ ...period, end: e.target.value })}
                />
              </label>
            </div>

            {/* Opening the measurement list, reading two dates and typing
                them back in is a chore the wizard can do itself. Each button
                states the dates it will use, so nothing happens unseen. */}
            {shortcuts.length > 0 && (
              <div className="wiz__shortcuts">
                {shortcuts.map((shortcut) => {
                  const on =
                    period.start === shortcut.start && period.end === shortcut.end;
                  return (
                    <button
                      key={shortcut.key}
                      type="button"
                      className={on ? 'wiz__shortcut wiz__shortcut--on' : 'wiz__shortcut'}
                      onClick={() =>
                        setPeriod({ start: shortcut.start, end: shortcut.end })
                      }
                      aria-pressed={on}
                    >
                      <span className="wiz__shortcut-title">{shortcut.title}</span>
                      <span className="wiz__shortcut-meta">
                        {formatDateLong(shortcut.start)} – {formatDateLong(shortcut.end)}{' '}
                        · {daysBetween(shortcut.start, shortcut.end)} gün
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            {dates !== null && shortcuts.length === 0 && (
              <p className="wiz__hint">
                Bu öğrencinin karşılaştırılacak ikinci bir kaydı yok; dönemi kendiniz
                seçin.
              </p>
            )}
          </div>
        )}

        {step >= 2 && source && selections && snapshot && (
          <>
            {step === 2 && (
              <MetricsStep
                metrics={offeredMetrics}
                customMetrics={offeredCustomMetrics}
                selections={selections}
                onChange={update}
              />
            )}
            {step === 3 && (
              <PhotosStep
                source={source}
                selections={selections}
                photoUrls={prepared?.photoUrls ?? {}}
                onChange={update}
              />
            )}
            {step === 4 && (
              <PostureStep
                source={source}
                selections={selections}
                photoUrls={prepared?.photoUrls ?? {}}
                onChange={update}
              />
            )}
            {step === 5 && (
              <GoalsStep
                studentId={studentId}
                source={source}
                selections={selections}
                onChange={update}
                onAdded={(goal) =>
                  setPrepared((current) =>
                    current
                      ? {
                          ...current,
                          source: {
                            ...current.source,
                            goals: [...current.source.goals, goal],
                          },
                        }
                      : current,
                  )
                }
              />
            )}
            {step === 6 && (
              <AssessmentStep
                studentId={studentId}
                source={source}
                selections={selections}
                onChange={update}
                onEdited={(assessment) =>
                  setPrepared((current) =>
                    current
                      ? {
                          ...current,
                          source: {
                            ...current.source,
                            assessments: current.source.assessments.map((a) =>
                              a.id === assessment.id ? assessment : a,
                            ),
                          },
                        }
                      : current,
                  )
                }
              />
            )}
            {step === 7 && (
              <CoverStep
                options={prepared?.coverMessageOptions ?? []}
                selections={selections}
                onChange={update}
              />
            )}
            {step === 8 && <PackageStep selections={selections} onChange={update} />}
            {step === 9 && <ReadinessStep warnings={warnings} snapshot={snapshot} />}
            {step >= 10 && (
              <div className="wiz__preview">
                <h2 className="wiz__title">Önizleme</h2>
                <p className="wiz__hint">
                  Aşağıdaki, öğrenciye gidecek raporun kendisi. PDF birebir aynı
                  görünecek.
                </p>
                <div className="wiz__paper">
                  <ReportDocument
                    snapshot={snapshot}
                    resolveImage={resolveImage}
                    logoUrl={prepared?.logoUrl}
                  />
                </div>
                {step === 11 && (
                  <div className="wiz__generate">
                    {generated ? (
                      <>
                        <p className="wiz__generated">
                          Rapor oluşturuldu — <b>sürüm {generated.versionNumber}</b>.
                        </p>
                        <div className="wiz__generate-actions">
                          <a
                            className="btn btn--primary"
                            href={generated.pdfUrl ?? '#'}
                            target="_blank"
                            rel="noreferrer"
                          >
                            PDF'i Aç
                          </a>
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={() => navigate('/raporlar')}
                          >
                            Raporlara Git
                          </Button>
                        </div>
                        <p className="wiz__hint">
                          Bu sürüm değişmez. Bir düzeltme gerekirse yeni bir sürüm
                          oluşturursunuz; öğrenciye gönderdiğiniz bu dosya olduğu gibi
                          kalır.
                        </p>
                      </>
                    ) : (
                      <>
                        <Button type="button" loading={generating} onClick={generate}>
                          Raporu Oluştur
                        </Button>
                        <p className="wiz__hint">
                          PDF yalnızca burada üretilir — önizlemede, adımlarda ya da
                          geçmişte değil. Birkaç saniye sürebilir.
                        </p>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <div className="wiz__nav">
        <Button
          type="button"
          variant="secondary"
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
        >
          Geri
        </Button>
        <Button
          type="button"
          loading={busy}
          disabled={!canAdvance || step === STEPS.length - 1}
          onClick={() => {
            if (step === 1) void prepare();
            else setStep((s) => Math.min(STEPS.length - 1, s + 1));
          }}
        >
          {step === 1 ? 'Verileri Getir' : 'İleri'}
        </Button>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ steps */

function MetricsStep({
  metrics,
  customMetrics,
  selections,
  onChange,
}: {
  metrics: ReportSnapshot['metrics'];
  /** The student's own custom measurement fields — e.g. "Boyun", "El Bileği". */
  customMetrics: SnapshotCustomMetric[];
  selections: ReportSelections;
  onChange: (patch: Partial<ReportSelections>) => void;
}) {
  function toggle(key: MetricKey) {
    const on = selections.includedMetrics.includes(key);
    onChange({
      includedMetrics: on
        ? selections.includedMetrics.filter((k) => k !== key)
        : [...selections.includedMetrics, key],
    });
  }

  function toggleCustom(labelNormalized: string) {
    const on = selections.includedCustomLabels.includes(labelNormalized);
    onChange({
      includedCustomLabels: on
        ? selections.includedCustomLabels.filter((l) => l !== labelNormalized)
        : [...selections.includedCustomLabels, labelNormalized],
    });
  }

  return (
    <div>
      <h2 className="wiz__title">Ölçümler</h2>
      <p className="wiz__hint">
        Rapora girecek ölçümleri seçin. Başlangıç ve güncel değerler, seçtiğiniz dönem
        içindeki ilk ve son kayıttan gelir.
      </p>

      {metrics.length === 0 && customMetrics.length === 0 ? (
        <p className="wiz__empty">Bu öğrenci için ölçüm bulunmuyor.</p>
      ) : (
        <ul className="wiz__list">
          {metrics.map((metric) => (
            <li className="wiz__row" key={metric.key}>
              <label className="wiz__check">
                <input
                  type="checkbox"
                  checked={selections.includedMetrics.includes(metric.key)}
                  onChange={() => toggle(metric.key)}
                />
                <span>{metric.label}</span>
              </label>
              <span className="wiz__values">
                {metric.start
                  ? formatValue(metric.start.value, metric.unit, metric.decimals)
                  : '—'}
                <span aria-hidden="true"> → </span>
                {formatValue(metric.current.value, metric.unit, metric.decimals)}
                <strong>
                  {metric.changeAbs === null
                    ? ''
                    : formatDelta(metric.changeAbs, metric.unit, metric.decimals)}
                </strong>
              </span>
            </li>
          ))}

          {/* The studio's own fields — not named by the product, so they
              cannot live in the fixed list above. Same row shape. */}
          {customMetrics.map((metric) => (
            <li className="wiz__row" key={metric.labelNormalized}>
              <label className="wiz__check">
                <input
                  type="checkbox"
                  checked={selections.includedCustomLabels.includes(
                    metric.labelNormalized,
                  )}
                  onChange={() => toggleCustom(metric.labelNormalized)}
                />
                <span>{metric.label}</span>
              </label>
              <span className="wiz__values">
                {metric.start
                  ? formatValue(metric.start.value, metric.unit, metric.decimals)
                  : '—'}
                <span aria-hidden="true"> → </span>
                {formatValue(metric.current.value, metric.unit, metric.decimals)}
                <strong>
                  {metric.changeAbs === null
                    ? ''
                    : formatDelta(metric.changeAbs, metric.unit, metric.decimals)}
                </strong>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PhotosStep({
  source,
  selections,
  photoUrls,
  onChange,
}: {
  source: ReportSource;
  selections: ReportSelections;
  photoUrls: Record<string, string>;
  onChange: (patch: Partial<ReportSelections>) => void;
}) {
  // Only what can actually reach the report. Offering a photo the snapshot
  // will drop is worse than not offering it: the instructor picks it, and the
  // report silently comes back with one frame instead of two.
  const inPeriod = source.photos.filter(
    (photo) =>
      photo.capturedAt >= source.period.start && photo.capturedAt <= source.period.end,
  );

  const groups = new Map<string, PhotoRecord[]>();
  for (const photo of inPeriod) {
    const key =
      photo.category === 'custom' ? `custom:${photo.customLabel}` : photo.category;
    groups.set(key, [...(groups.get(key) ?? []), photo]);
  }

  function setPair(category: string, leftId: string | null, rightId: string | null) {
    const others = selections.photoPairs.filter((p) => {
      const list = groups.get(category) ?? [];
      return !list.some((photo) => photo.id === p.rightPhotoId);
    });
    if (!rightId) {
      onChange({ photoPairs: others });
      return;
    }
    const photo = source.photos.find((p) => p.id === rightId);
    if (!photo) return;
    onChange({
      photoPairs: [
        ...others,
        { category: photo.category, leftPhotoId: leftId, rightPhotoId: rightId },
      ],
    });
  }

  return (
    <div>
      <h2 className="wiz__title">Fotoğraflar</h2>
      <p className="wiz__hint">
        Her bölge için karşılaştırılacak iki fotoğrafı seçin. Yalnızca dönem içinde
        çekilmiş fotoğraflar listelenir.
      </p>

      {groups.size === 0 ? (
        <p className="wiz__empty">
          {source.photos.length > 0
            ? 'Bu öğrencinin fotoğrafları var ama hiçbiri seçilen dönemde çekilmemiş.'
            : 'Bu öğrencinin gelişim fotoğrafı yok.'}
        </p>
      ) : (
        <div className="wiz__photogroups">
          {[...groups.entries()].map(([key, list]) => {
            const sorted = [...list].sort((a, b) =>
              a.capturedAt < b.capturedAt ? -1 : 1,
            );
            const first = sorted[0];
            const label =
              first?.category === 'custom' && first.customLabel
                ? first.customLabel
                : PHOTO_CATEGORY_LABELS[first?.category ?? 'front'];

            const pair = selections.photoPairs.find((p) =>
              sorted.some((photo) => photo.id === p.rightPhotoId),
            );

            return (
              <div className="wiz__photogroup" key={key}>
                <div className="wiz__photohead">
                  <span className="wiz__photolabel">{label}</span>
                  <label className="wiz__check">
                    <input
                      type="checkbox"
                      checked={pair !== undefined}
                      onChange={() =>
                        pair
                          ? setPair(key, null, null)
                          : setPair(
                              key,
                              sorted.length > 1 ? (sorted[0]?.id ?? null) : null,
                              sorted[sorted.length - 1]?.id ?? null,
                            )
                      }
                    />
                    <span>Rapora ekle</span>
                  </label>
                </div>

                {pair && (
                  <div className="wiz__photopick">
                    <label>
                      <span>Başlangıç</span>
                      <select
                        value={pair.leftPhotoId ?? ''}
                        onChange={(e) =>
                          setPair(key, e.target.value || null, pair.rightPhotoId)
                        }
                      >
                        <option value="">Karşılaştırma yok</option>
                        {sorted.map((photo) => (
                          <option key={photo.id} value={photo.id}>
                            {formatDateLong(photo.capturedAt)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>Güncel</span>
                      <select
                        value={pair.rightPhotoId}
                        onChange={(e) => setPair(key, pair.leftPhotoId, e.target.value)}
                      >
                        {sorted.map((photo) => (
                          <option key={photo.id} value={photo.id}>
                            {formatDateLong(photo.capturedAt)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className="wiz__thumbs">
                      {pair.leftPhotoId && (
                        <img src={photoUrls[pair.leftPhotoId]} alt="" />
                      )}
                      <img src={photoUrls[pair.rightPhotoId]} alt="" />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {selections.photoPairs.some(
        (p) => !['front', 'side', 'back'].includes(p.category),
      ) && (
        <label className="wiz__note">
          <span>
            Bölgesel değerlendirme notu{' '}
            <em>(opsiyonel, rapora sizin sözlerinizle girer)</em>
          </span>
          <textarea
            rows={3}
            maxLength={300}
            value={selections.regionalNote ?? ''}
            onChange={(e) => onChange({ regionalNote: e.target.value })}
            placeholder="Bel bölgesinde belirgin bir incelme gözlemledim."
          />
        </label>
      )}
    </div>
  );
}

function PostureStep({
  source,
  selections,
  photoUrls,
  onChange,
}: {
  source: ReportSource;
  selections: ReportSelections;
  photoUrls: Record<string, string>;
  onChange: (patch: Partial<ReportSelections>) => void;
}) {
  const selectedIds = selections.postureAssessmentIds;
  const atCap = selectedIds.length >= POSTURE_COMPARISON_MAX;

  function toggle(id: string) {
    const on = selectedIds.includes(id);
    onChange({
      postureAssessmentIds: on
        ? selectedIds.filter((p) => p !== id)
        : [...selectedIds, id],
    });
  }

  return (
    <div>
      <h2 className="wiz__title">Postür Analizi</h2>
      <p className="wiz__hint">
        Rapora girecek postür analizlerini seçin (en fazla {POSTURE_COMPARISON_MAX}).
        Seçilen her analiz raporda kendi sayfasında, eskiden yeniye sırayla yer alır.
        Analizler Postür Analizi sekmesinden eklenir ve düzenlenir.
      </p>

      {source.postureAssessments.length === 0 ? (
        <p className="wiz__empty">Bu öğrencinin postür analizi yok.</p>
      ) : (
        <ul className="wiz__list">
          <li className="wiz__row">
            <label className="wiz__check">
              <input
                type="checkbox"
                checked={selectedIds.length === 0}
                onChange={() => onChange({ postureAssessmentIds: [] })}
              />
              <span>Rapora eklenmesin</span>
            </label>
          </li>

          {source.postureAssessments.map((p) => {
            const checked = selectedIds.includes(p.id);
            const disabled = !checked && atCap;
            return (
              <li className="wiz__assess" key={p.id}>
                <div className="wiz__assess-head">
                  <label
                    className={
                      disabled ? 'wiz__check wiz__check--disabled' : 'wiz__check'
                    }
                    title={
                      disabled
                        ? `En fazla ${POSTURE_COMPARISON_MAX} analiz karşılaştırılabilir`
                        : undefined
                    }
                  >
                    <input
                      type="checkbox"
                      name="posture"
                      checked={checked}
                      disabled={disabled}
                      onChange={() => toggle(p.id)}
                    />
                    <span>{formatDateLong(p.assessedAt)}</span>
                  </label>
                </div>
                <div className="wiz__assess-body">
                  {p.photos.length > 0 && (
                    <div className="wiz__posture-thumbs">
                      {p.photos.map((photo) => (
                        <img
                          key={photo.id}
                          src={photoUrls[photo.id]}
                          alt={photo.angle}
                          className="wiz__posture-thumb"
                        />
                      ))}
                    </div>
                  )}
                  {p.score !== null && (
                    <p className="wiz__assess-note">
                      <em>Genel Postür</em> {p.score}/5
                    </p>
                  )}
                  {p.note && (
                    <p className="wiz__assess-note">
                      <em>Not</em> {p.note}
                    </p>
                  )}
                  {p.photos.length === 0 && p.score === null && !p.note && (
                    <p className="wiz__assess-note">Bu analiz boş.</p>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function GoalsStep({
  studentId,
  source,
  selections,
  onChange,
  onAdded,
}: {
  studentId: string;
  source: ReportSource;
  selections: ReportSelections;
  onChange: (patch: Partial<ReportSelections>) => void;
  onAdded: (goal: GoalRecord) => void;
}) {
  const [adding, setAdding] = useState(false);
  const goals = source.goals.filter((g) => g.status !== 'cancelled');

  function toggle(id: string) {
    const on = selections.goalIds.includes(id);
    onChange({
      goalIds: on
        ? selections.goalIds.filter((g) => g !== id)
        : [...selections.goalIds, id],
    });
  }

  return (
    <div>
      <div className="wiz__row-head">
        <h2 className="wiz__title">Hedefler</h2>
        {!adding && (
          <button type="button" className="wiz__link" onClick={() => setAdding(true)}>
            + Hedef Ekle
          </button>
        )}
      </div>
      <p className="wiz__hint">
        Rapora girecek hedefleri seçin. Sayı sınırı yok; çok seçerseniz rapor sonraki
        sayfalara taşar. Burada eklediğiniz bir hedef öğrencinin kaydına da işlenir.
      </p>

      {adding && (
        <GoalForm
          studentId={studentId}
          records={source.measurements}
          onCancel={() => setAdding(false)}
          onSaved={(saved) => {
            setAdding(false);
            onAdded({ ...saved, status: 'active' });
            onChange({ goalIds: [...selections.goalIds, saved.id] });
          }}
        />
      )}

      {goals.length === 0 ? (
        <p className="wiz__empty">Bu öğrencinin hedefi yok.</p>
      ) : (
        <ul className="wiz__list">
          {goals.map((goal) => (
            <li className="wiz__row" key={goal.id}>
              <label className="wiz__check">
                <input
                  type="checkbox"
                  checked={selections.goalIds.includes(goal.id)}
                  onChange={() => toggle(goal.id)}
                />
                <span>{goal.title}</span>
              </label>
              <span className="wiz__values">
                {goal.goalMode === 'numeric'
                  ? `${goal.startValue ?? '—'} → ${goal.currentValue ?? '—'} / ${goal.targetValue ?? '—'} ${goal.unit ?? ''}`
                  : (goal.qualitativeNote ?? '')}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AssessmentStep({
  studentId,
  source,
  selections,
  onChange,
  onEdited,
}: {
  studentId: string;
  source: ReportSource;
  selections: ReportSelections;
  onChange: (patch: Partial<ReportSelections>) => void;
  onEdited: (assessment: AssessmentRecord) => void;
}) {
  const [editing, setEditing] = useState<string | null>(null);

  return (
    <div>
      <h2 className="wiz__title">Eğitmen Değerlendirmesi</h2>
      <p className="wiz__hint">
        Rapora girecek değerlendirmeyi seçin. Bir önceki değerlendirmeyle karşılaştırma
        otomatik yapılır. İçeriğini burada görebilir, gerekiyorsa düzeltebilirsiniz —
        düzeltme öğrencinin kaydına da işlenir.
      </p>

      {source.assessments.length === 0 ? (
        <p className="wiz__empty">Bu öğrencinin değerlendirmesi yok.</p>
      ) : (
        <ul className="wiz__list">
          <li className="wiz__row">
            <label className="wiz__check">
              <input
                type="radio"
                name="assessment"
                checked={selections.assessmentId === null}
                onChange={() => onChange({ assessmentId: null })}
              />
              <span>Rapora eklenmesin</span>
            </label>
          </li>

          {source.assessments.map((a) => {
            const scored = ASSESSMENT_KEYS.filter(
              (key) => a[key as keyof AssessmentRecord] !== null,
            );
            return (
              <li className="wiz__assess" key={a.id}>
                <div className="wiz__assess-head">
                  <label className="wiz__check">
                    <input
                      type="radio"
                      name="assessment"
                      checked={selections.assessmentId === a.id}
                      onChange={() => onChange({ assessmentId: a.id })}
                    />
                    <span>{formatDateLong(a.assessedAt)}</span>
                  </label>
                  {editing !== a.id && (
                    <button
                      type="button"
                      className="wiz__link"
                      onClick={() => setEditing(a.id)}
                    >
                      Düzenle
                    </button>
                  )}
                </div>

                {editing === a.id ? (
                  <AssessmentForm
                    studentId={studentId}
                    initial={a}
                    onCancel={() => setEditing(null)}
                    onSaved={(saved) => {
                      onEdited(saved);
                      setEditing(null);
                    }}
                  />
                ) : (
                  /* A date alone is not enough to choose between two
                     assessments — the instructor has to see what is in them. */
                  <div className="wiz__assess-body">
                    {scored.length > 0 && (
                      <ul className="wiz__scores">
                        {scored.map((key) => (
                          <li key={key}>
                            <span>{ASSESSMENT_LABELS[key as AssessmentKey]}</span>
                            <strong>
                              {a[key as keyof AssessmentRecord] as number}/5
                            </strong>
                          </li>
                        ))}
                      </ul>
                    )}
                    {a.generalNote && (
                      <p className="wiz__assess-note">
                        <em>Genel Değerlendirme</em> {a.generalNote}
                      </p>
                    )}
                    {a.nextPeriodFocus && (
                      <p className="wiz__assess-note">
                        <em>Bir Sonraki Dönem Odağı</em> {a.nextPeriodFocus}
                      </p>
                    )}
                    {scored.length === 0 && !a.generalNote && !a.nextPeriodFocus && (
                      <p className="wiz__assess-note">Bu değerlendirme boş.</p>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function CoverStep({
  options,
  selections,
  onChange,
}: {
  options: CoverMessage[];
  selections: ReportSelections;
  onChange: (patch: Partial<ReportSelections>) => void;
}) {
  const own = selections.coverMessageCustom !== null;

  return (
    <div>
      <h2 className="wiz__title">Kapak Mesajı</h2>
      <p className="wiz__hint">
        Raporun kapağında görünecek cümle. Hazır listeden seçebilir ya da kendiniz
        yazabilirsiniz.
      </p>

      <ul className="wiz__list wiz__list--messages">
        <li className="wiz__row">
          <label className="wiz__check">
            <input
              type="radio"
              name="cover"
              checked={!own && selections.coverMessageKey === null}
              onChange={() =>
                onChange({ coverMessageKey: null, coverMessageCustom: null })
              }
            />
            <span>Mesaj ekleme</span>
          </label>
        </li>
        {options.map((message) => (
          <li className="wiz__row" key={message.key}>
            <label className="wiz__check">
              <input
                type="radio"
                name="cover"
                checked={!own && selections.coverMessageKey === message.key}
                onChange={() =>
                  onChange({ coverMessageKey: message.key, coverMessageCustom: null })
                }
              />
              <span className="wiz__message">{message.text}</span>
            </label>
          </li>
        ))}

        {/* Constitution v1.3 §8, exception 3. The curated list stays first so
            it remains the easy path, and the guidance below travels with the
            field rather than living in a manual nobody opens. */}
        <li className="wiz__row wiz__row--own">
          <label className="wiz__check">
            <input
              type="radio"
              name="cover"
              checked={own}
              onChange={() => onChange({ coverMessageKey: null, coverMessageCustom: '' })}
            />
            <span>Kendim yazacağım</span>
          </label>
        </li>
      </ul>

      {own && (
        <label className="wiz__note">
          <span>
            Kapak mesajınız{' '}
            <em>
              (öğrenciye bu haliyle gider, en fazla {COVER_MESSAGE_MAX_LENGTH} karakter)
            </em>
          </span>
          <textarea
            rows={3}
            maxLength={COVER_MESSAGE_MAX_LENGTH}
            value={selections.coverMessageCustom ?? ''}
            onChange={(e) => onChange({ coverMessageCustom: e.target.value })}
            placeholder="Bu dönem gösterdiğiniz düzen gerçekten fark yarattı."
            autoFocus
          />
          <span className="wiz__count">
            {(selections.coverMessageCustom ?? '').length} / {COVER_MESSAGE_MAX_LENGTH}
          </span>
          <span className="wiz__hint">
            Sayı vaadi, sağlık iddiası ve bedene dair yorum yazmayın — bu cümle öğrenciye
            doğrudan gider.
          </span>
        </label>
      )}
    </div>
  );
}

function PackageStep({
  selections,
  onChange,
}: {
  selections: ReportSelections;
  onChange: (patch: Partial<ReportSelections>) => void;
}) {
  const { enabled, remainingLessons } = selections.packageReminder;

  return (
    <div>
      <h2 className="wiz__title">Paket Hatırlatması</h2>
      <p className="wiz__hint">
        Rapora bir paket hatırlatması eklemek ister misiniz? Girdiğiniz sayı yalnızca bu
        rapora yazılır — sistemde paket bakiyesi tutulmaz.
      </p>

      <div className="wiz__toggle">
        <label className={enabled ? 'chip chip--on' : 'chip'}>
          <input
            type="radio"
            name="package"
            checked={enabled}
            onChange={() =>
              onChange({ packageReminder: { enabled: true, remainingLessons } })
            }
            className="pp-visually-hidden"
          />
          Evet
        </label>
        <label className={!enabled ? 'chip chip--on' : 'chip'}>
          <input
            type="radio"
            name="package"
            checked={!enabled}
            onChange={() =>
              onChange({ packageReminder: { enabled: false, remainingLessons: null } })
            }
            className="pp-visually-hidden"
          />
          Hayır
        </label>
      </div>

      {enabled && (
        <NumberField
          className="wiz__lessons"
          label="Kalan ders sayısı"
          limit={LIMITS.remainingLessons}
          required
          value={remainingLessons === null ? '' : String(remainingLessons)}
          onChange={(raw) => {
            const parsed = parseTypedNumber(raw);
            onChange({
              packageReminder: {
                enabled: true,
                remainingLessons: typeof parsed === 'number' ? parsed : null,
              },
            });
          }}
        />
      )}
      {enabled && remainingLessons === null && (
        <p className="wiz__hint wiz__hint--warn" role="alert">
          Ders sayısını girmeden devam edemezsiniz — rakam olmadan hatırlatma cümlesinin
          ortası boş kalır.
        </p>
      )}
    </div>
  );
}

function ReadinessStep({
  warnings,
  snapshot,
}: {
  warnings: { code: string; message: string }[];
  snapshot: ReturnType<typeof buildSnapshot>;
}) {
  const metricCount = snapshot.metrics.length + snapshot.customMetrics.length;
  const ready = [
    metricCount > 0 && `${metricCount} ölçüm`,
    snapshot.fullBodyPhotoPairs.length + snapshot.regionalPhotoPairs.length > 0 &&
      `${snapshot.fullBodyPhotoPairs.length + snapshot.regionalPhotoPairs.length} fotoğraf karşılaştırması`,
    snapshot.goals.length > 0 && `${snapshot.goals.length} hedef`,
    snapshot.assessment && 'Eğitmen değerlendirmesi',
    snapshot.packageReminder.enabled && 'Paket hatırlatması',
  ].filter((value): value is string => typeof value === 'string');

  return (
    <div>
      <h2 className="wiz__title">Veri Hazırlık Özeti</h2>
      <p className="wiz__hint">
        Uyarılar rapor oluşturmanızı engellemez — eksik bölümler rapordan kendiliğinden
        çıkar.
      </p>

      <ul className="wiz__readiness">
        {ready.map((item) => (
          <li className="wiz__ready" key={item}>
            <span aria-hidden="true">✓</span> {item}
          </li>
        ))}
        {warnings.map((warning) => (
          <li className="wiz__warn" key={warning.code}>
            <span aria-hidden="true">!</span> {warning.message}
          </li>
        ))}
      </ul>
    </div>
  );
}
